import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import type { SerializedPresaveSubscribeWidget } from 'fym-shared/src/smartlinks/types';
import { TypedMemo } from '../../../common/types';
import { PresaveSubscribeServiceRow } from '../../widgets/views/presaveSubscribeWidget/PresaveSubscribeServiceRow';
import {
  PRESAVE_SUBSCRIBE_SERVICES,
  toggleService,
} from '../../widgets/views/presaveSubscribeWidget/presaveSubscribeServices';
import { presaveSubscribeMessages as messages } from '../../widgets/views/presaveSubscribeWidget/presaveSubscribeMessages';
import { BlockAutosave } from './useBlockAutosave';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole card; each part uses its own slice of it */
import subscribeStyles from '../../widgets/views/presaveSubscribeWidget/PresaveSubscribeWidget.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SubscribeBlockFormProps {
  widget: SerializedPresaveSubscribeWidget;
  autosave: BlockAutosave;
}

/** "Get every release" (design d8): the services fans can pick. `{ services }`; copy from the old editor. */
const SubscribeBlockFormComponent: React.FC<SubscribeBlockFormProps> = ({ widget, autosave }) => {
  const intl = useIntl();
  const [services, setServices] = useState<string[]>(() =>
    PRESAVE_SUBSCRIBE_SERVICES.filter((one) => widget.data.services.includes(one))
  );
  const { save, markInvalid } = autosave;

  const onToggle = useCallback(
    (integrationType: string) => {
      const next = toggleService(services, integrationType);
      setServices(next);
      if (next.length === 0) {
        markInvalid();
        return;
      }
      save({ services: next });
    },
    [markInvalid, save, services]
  );

  return (
    <div className={subscribeStyles.form} data-testid={`form-${SmartLinkWidgetType.presaveSubscribe}`}>
      <p className={subscribeStyles.help}>{intl.formatMessage(messages.help)}</p>
      <span className={subscribeStyles.label}>{intl.formatMessage(messages.servicesLabel)}</span>
      <div className={subscribeStyles.list}>
        {PRESAVE_SUBSCRIBE_SERVICES.map((integrationType) => (
          <PresaveSubscribeServiceRow
            key={integrationType}
            integrationType={integrationType}
            checked={services.includes(integrationType)}
            disabled={false}
            onToggle={onToggle}
          />
        ))}
      </div>
      {services.length === 0 ? (
        <p className={subscribeStyles.error} role="alert">
          {intl.formatMessage(messages.noService)}
        </p>
      ) : (
        <p className={subscribeStyles.help}>{intl.formatMessage(messages.emailNote)}</p>
      )}
    </div>
  );
};

export const SubscribeBlockForm = TypedMemo(SubscribeBlockFormComponent);
